import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ChartQuery} from 'entities/chart';
import {TableContent} from '@/views/storyboard/charts/table/TableContent';
import {scalableOutputTypes} from '@/views/storyboard/utils/chartConstants';
import {getScaledChartData} from '@/views/storyboard/utils/calculations/chartScaling';
import {TableChartData} from '@/views/storyboard/cellhandlers/TableCellHandler';
import {notRenderedKeysArray, sortLegendKeys} from '@/views/storyboard/charts/utils/SortLegendKeys';
import {InlineAlert} from '@/components/alerts';
import i18n from 'i18n';

function useContainerSize(ref: React.RefObject<HTMLDivElement | null>): {width: number; height: number} {
    const [size, setSize] = useState({width: 0, height: 0});

    const measure = useCallback(() => {
        if (ref.current) {
            const {width, height} = ref.current.getBoundingClientRect();
            setSize((prev) =>
                prev.width === Math.round(width) && prev.height === Math.round(height)
                    ? prev
                    : {width: Math.round(width), height: Math.round(height)},
            );
        }
    }, [ref]);

    useEffect(() => {
        measure();
        if (!ref.current) return;

        const observer = new ResizeObserver(measure);
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [ref, measure]);

    return size;
}

interface Props {
    chartId?: number | null;
    data: TableChartData[];
    scalingFactor?: string;
    chartQuery: ChartQuery;
    chartTitle: string;
    queryDuration?: string;
    chartSettingsView?: boolean;
}

export const TableChartWrapper = React.memo<Props>((
    {
        chartId = null,
        data,
        scalingFactor,
        chartQuery,
        chartTitle,
        queryDuration = '0',
        chartSettingsView = false,
    },
) => {
    const distinctKeys = useMemo(() => {
        let filteredKeys: string[] = [];

        const getDistinctValues = (key: string): void => {
            if (!filteredKeys.concat(notRenderedKeysArray).includes(key)) {
                filteredKeys.push(key);
            }
        };

        for (let i = 0; i < data.length; i++) {
            Object.keys(data[i]).forEach((key) => getDistinctValues(key));
        }

        // Replace all dots with a lookalike unicode symbol because otherwise they get processed as nested objects
        // by react-boostrap-table-next
        filteredKeys = filteredKeys.map((key) => key.replace('.', '\u2024'));

        if (filteredKeys.length == 0) {
            return filteredKeys
        } else if (filteredKeys[0].includes(' to ')) {
            return sortLegendKeys(filteredKeys)
        } else {
            return [...filteredKeys.slice(1), filteredKeys[0]];
        }
    }, [data]);


    let processedData = data;

    processedData = processedData.map((row: TableChartData): TableChartData => {
        const rowUndefinedRemoved: Partial<TableChartData> = {};

        // Replace all dots with a lookalike unicode symbol because otherwise they get processed as nested objects
        // by react-boostrap-table-next
        Object.keys(row).forEach((key) => {
            const cleanKey = key.replace('.', '\u2024');
            rowUndefinedRemoved[cleanKey as keyof TableChartData] = row[key as keyof TableChartData];
        });

        // Coalesce all undefined and null values to empty strings
        distinctKeys.forEach((key) => {
            rowUndefinedRemoved[key as keyof TableChartData] = rowUndefinedRemoved[key as keyof TableChartData] ?? '';
        });

        return rowUndefinedRemoved as TableChartData;
    });

    if (scalingFactor && scalableOutputTypes.some(output => output === chartQuery.output)) {
        processedData = getScaledChartData(processedData, distinctKeys, scalingFactor);
    }

    // If there are no distinct keys, show a warning message instead of the table (metric timeline)
    if (distinctKeys.length == 0) {
        return (
            <InlineAlert 
                variant="warning" 
                message={i18n.t('jt_this_query.text')}
                className="mt-4 mx-auto w-75"
            />
        )
    }

    const containerRef = useRef<HTMLDivElement>(null);
    const {width, height} = useContainerSize(containerRef);

    return (
        <>
            <div ref={containerRef} style={{flex: 1, minHeight: 0, width: '100%'}}>
                {width > 0 && height > 0 && (
                    <TableContent
                        width={width}
                        height={height}
                        data={processedData}
                        chartId={chartId}
                        chartTitle={chartTitle}
                        distinctKeys={distinctKeys}
                        aggregationField={
                            ['terms_timeline', 'terms'].some(
                                output => output === chartQuery.output
                            ) ? chartQuery.aggs_item : ''
                        }
                        output={chartQuery.output}
                        aggregationFields={chartQuery.aggs_item_list}
                    />
                )}
            </div>
            {!chartSettingsView ? (
                <div className="chart-query-time-indicator">
                    <p>
                        {i18n.t('table_chart_query_duration.text', {queryDuration})}
                    </p>
                </div>
            ) : null}
        </>
    );
});

